import { useCallback, useEffect, useState } from "react";
import { useInView } from "react-intersection-observer";
import { Form, Formik } from "formik";
import { Link, useNavigate } from "react-router-dom";
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query";
import { toast } from "react-toastify";

import { enums } from "../../constants/enum";
import useOrderService from "../../services/api/orderService";
import dateFormatter from "../../util/dateFormatter";

import {
  AddIcon,
  ArrowLongRightIcon,
  CloseIcon,
  FlagLineIcon,
  FlagSolidIcon,
  SearchIcon,
} from "../../assets/icons";
import { Button, Chip, Loading, PageTitle, Section } from "../../components";
import { DateInput, SelectInput, TextInput } from "../../components/forms";
import "./OrdersPage.scss";

function OrdersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { ref, inView } = useInView();
  const { getOrders, queryKey, toggleOrderFlag } = useOrderService();

  const [filters, setFilters] = useState({
    orderNumber: "",
    status: "",
    from: "",
    to: "",
  });

  const { data, isLoading, error, fetchNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: [queryKey, filters],
      queryFn: ({ pageParam = 1 }) =>
        getOrders(
          filters.status,
          filters.from,
          filters.to,
          filters.orderNumber,
          "",
          pageParam,
        ),
      initialPageParam: 1,
      getNextPageParam: (lastPage) => {
        if (lastPage?.totalPages > lastPage?.currentPage) {
          return lastPage?.currentPage + 1;
        }
        return undefined;
      },
    });

  const toggleFlagMutation = useMutation({
    mutationFn: toggleOrderFlag,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      toast.success("Order flag updated");
    },
    onError: (err) => {
      toast.error(err.response?.data?.message || "Failed to update flag");
    },
  });

  const handleSearch = useCallback((values) => {
    setFilters(values);
  }, []);

  useEffect(() => {
    if (inView) {
      fetchNextPage();
    }
  }, [fetchNextPage, inView]);

  function getOrderStatusChipColor(orderStatus) {
    switch (orderStatus) {
      case enums.ORDER_STATUSES.READY:
        return "info";
      case enums.ORDER_STATUSES.CONFIRMED:
        return "warning";
      case enums.ORDER_STATUSES.COMPLETED:
        return "success";
      case enums.ORDER_STATUSES.CANCELLED:
        return "danger";
      default:
        return "default";
    }
  }

  if (isLoading) {
    return <Loading variant="fullScreen" />;
  }

  if (error) {
    toast.error(error.message || enums.DEFAULT_ERROR_MESSAGE);
  }

  return (
    <div className="page orders-page">
      <PageTitle text="Orders">
        <Button
          text="Add New Order"
          icon={<AddIcon />}
          onClick={() => navigate("/orders/add")}
        />
      </PageTitle>

      <Section title="Search">
        <Formik
          initialValues={{ orderNumber: "", status: "", from: "", to: "" }}
          onSubmit={(values) => handleSearch(values)}
          onReset={() =>
            setFilters({ orderNumber: "", status: "", from: "", to: "" })
          }
        >
          <Form>
            <div className="input-group">
              <TextInput
                name="orderNumber"
                label="Search by Order Number"
                placeholder="Enter order number"
              />
              <SelectInput
                name="status"
                label="Order Status"
                values={[
                  { label: "Any", value: "" },
                  ...Object.values(enums.ORDER_STATUSES).map((status) => ({
                    label: status,
                    value: status,
                  })),
                ]}
              />
            </div>

            <div className="input-group">
              <DateInput label="From" name="from" />
              <DateInput label="To" name="to" />
            </div>

            <div className="form-actions">
              <Button text="Search" icon={<SearchIcon />} type="submit" />
              <Button
                text="Clear"
                icon={<CloseIcon />}
                type="reset"
                outlined
                className="clear-btn"
              />
            </div>
          </Form>
        </Formik>

        <div className="search-results">
          <div className="order-list">
            <div className="table-title">
              <h3>Search Results</h3>
            </div>

            <div className="table-content">
              <table>
                <thead>
                  <tr>
                    <th>Flag</th>
                    <th>Order ID</th>
                    <th>Order Date</th>
                    <th>Order Amount</th>
                    <th>Prescription ID</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>

                <tbody>
                  {data?.pages?.map(({orders}) =>
                    orders.map((order) => (
                      <tr key={order.orderId}>
                        <td>
                          <button
                            className="flag-btn"
                            onClick={() => toggleFlagMutation.mutate(order.orderId)}
                          >
                            {order.isFlagged ? (
                              <FlagSolidIcon />
                            ) : (
                              <FlagLineIcon />
                            )}
                          </button>
                        </td>
                        <td>{order.orderId}</td>
                        <td>{dateFormatter(order.orderDate)}</td>
                        <td>
                          {order.totalAmount
                            ? `${Number(order.totalAmount).toLocaleString()} LKR`
                            : "-"}
                        </td>
                        <td>{`${order.orderForm?.campaign?.orderNumberSequencePrefix || ""} ${order.orderForm.eyeCheckingNumber}`}</td>
                        <td>
                          <Chip
                            text={order.status}
                            variant={getOrderStatusChipColor(order.status)}
                          />
                        </td>
                        <td>
                          <Link to={`/orders/details/${order.orderId}`}>
                            <Button
                              text="View"
                              prefixIcon={<ArrowLongRightIcon />}
                            />
                          </Link>
                        </td>
                      </tr>
                    )),
                  )}
                </tbody>
              </table>
              <div
                style={{
                  width: "100%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  padding: "20px 0",
                }}
                ref={ref}
              >
                {isFetchingNextPage ? <Loading variant="inline" /> : null}
              </div>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

export default OrdersPage;
